import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createApiClient } from "../api";

const AppContext = createContext(null);

const TENANT_STORAGE_KEY = "ainaopen.tenant_id";
const SESSION_STORAGE_KEY = "ainaopen.session";

function readJSON(key) {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJSON(key, value) {
  if (typeof window === "undefined") return;
  if (value == null) window.localStorage.removeItem(key);
  else window.localStorage.setItem(key, JSON.stringify(value));
}

function readInitialTenant(session) {
  if (session && session.user && session.user.tenant_id) return session.user.tenant_id;
  const fromStorage = typeof window !== "undefined" ? window.localStorage.getItem(TENANT_STORAGE_KEY) : null;
  return fromStorage || import.meta.env.VITE_DEFAULT_TENANT_ID || "aina-dev";
}

export function AppProvider({ children }) {
  const [session, setSession] = useState(() => readJSON(SESSION_STORAGE_KEY));
  const [tenantId, setTenantIdState] = useState(() => readInitialTenant(session));

  const api = useMemo(() => {
    const client = createApiClient(tenantId);
    if (session && session.token && typeof client.setAuthToken === "function") {
      client.setAuthToken(session.token);
    }
    return client;
  }, [tenantId, session]);

  const setTenantId = useCallback((id) => {
    window.localStorage.setItem(TENANT_STORAGE_KEY, id);
    setTenantIdState(id);
  }, []);

  const setSessionAndPersist = useCallback((s) => {
    writeJSON(SESSION_STORAGE_KEY, s);
    setSession(s);
    if (s && s.user && s.user.tenant_id) {
      window.localStorage.setItem(TENANT_STORAGE_KEY, s.user.tenant_id);
      setTenantIdState(s.user.tenant_id);
    }
  }, []);

  const signOut = useCallback(() => {
    setSessionAndPersist(null);
  }, [setSessionAndPersist]);

  const refreshUser = useCallback(async () => {
    if (!session || !session.token) return;
    try {
      const me = await api.getMe();
      setSessionAndPersist({ ...session, user: me });
    } catch (err) {
      if (err && err.status === 401) setSessionAndPersist(null);
    }
  }, [api, session, setSessionAndPersist]);

  useEffect(() => {
    if (session && session.token) void refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session && session.token]);

  const user = session && session.user ? session.user : null;

  return (
    <AppContext.Provider
      value={{
        tenantId,
        setTenantId,
        api,
        user,
        session,
        setSession: setSessionAndPersist,
        signOut,
        refreshUser,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside <AppProvider>");
  return ctx;
}

export function useApi() {
  return useApp().api;
}

export const ROLE_LABELS = {
  tenant_admin: "Tenant Admin",
  product_manager: "Product Manager",
  ml_operator: "ML Operator",
  device_operator: "Device Operator",
  auditor: "Auditor",
};

export function hasRole(user, ...roles) {
  if (!user) return false;
  if (roles.length === 0) return true;
  // platform_admin passes every backend role check (shared/identity.py); it saw an empty menu here.
  if ((user.roles || []).includes("platform_admin")) return true;
  return roles.some((r) => user.roles.includes(r));
}
